/**
 * @file scheduleFlow.ts
 * @brief What happens after "Schedule" on Meeting Setup: one in-flight save,
 *        then Scheduled Meetings (replacing setup) with the new meeting
 *        highlighted and a confirmation; on failure stay with everything kept.
 *
 * @details
 * DES 10-10-2026 (UX fix): after scheduling, the app went back to Meeting
 * Setup with everything still filled in.
 *   1) one save at a time: a second tap while saving is ignored;
 *   2) success: replace setup with Scheduled Meetings, new meeting on top,
 *      highlighted, "Meeting scheduled · Sat 11 Oct, 1:00 PM · invites sent
 *      to 2 people" (no invites part when nobody else is invited);
 *   3) the setup form is reset (and gone from the stack, so Back returns to
 *      where setup was opened from);
 *   4) failure: stay, keep everything, show the error with Retry.
 * "Start now" is not part of this.
 *
 * The SAME file (keep them identical) lives in FRONTEND (src/features/meetings/scheduleFlow.ts) and
 * WEBPANEL (src/features/employee-boardroom/scheduleFlow.ts), so the phone,
 * the web and the desktop say and do the same thing. No imports: node:test
 * and jest both run it as is.
 *
 * @author DES
 * @date 10-10-2026
 */

export type ScheduledLike = {
  id: string;
  scheduled_at: string;
  organizer_email?: string | null;
  participants?: Array<{ email?: string | null }> | null;
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** "Sat 11 Oct, 1:00 PM" in the device's time zone; same on every platform. */
export function formatScheduledWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours();
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${DAYS[d.getDay()]} ${d.getDate()} ${
    MONTHS[d.getMonth()]
  }, ${hour12}:${mins} ${h < 12 ? 'AM' : 'PM'}`;
}

/** People who get the invite email: unique emails, the organiser not counted. */
export function invitedCount(meeting: ScheduledLike): number {
  const me = (meeting.organizer_email || '').trim().toLowerCase();
  const emails = new Set<string>();
  for (const p of meeting.participants || []) {
    const e = (p?.email || '').trim().toLowerCase();
    if (e && e !== me) emails.add(e);
  }
  return emails.size;
}

/** The confirmation line shown on Scheduled Meetings. */
export function scheduledMessage(meeting: ScheduledLike): string {
  const parts = [
    'Meeting scheduled',
    formatScheduledWhen(meeting.scheduled_at),
  ].filter(Boolean);
  const n = invitedCount(meeting);
  if (n > 0)
    parts.push(`invites sent to ${n} ${n === 1 ? 'person' : 'people'}`);
  return parts.join(' · ');
}

export const SCHEDULE_ERROR_TITLE = "Couldn't schedule the meeting";
export const SCHEDULE_ERROR_HINT = 'Everything you entered is kept. Try again.';

export type ScheduleDeps<M extends ScheduledLike> = {
  /** POST the meeting; throws on failure. */
  save: () => Promise<M>;
  /** Replace setup with Scheduled Meetings (never push on top of it). */
  showScheduled: (params: { highlightId: string; message: string }) => void;
  /** Clear title, participants, languages, agenda and tier. */
  resetForm: () => void;
  /** Stay on setup; show the error with a Retry that calls `retry`. */
  showError: (err: unknown, retry: () => void) => void;
  /** Loading on/off for the Schedule button. */
  setBusy?: (busy: boolean) => void;
};

/**
 * One Schedule press. Returns a function that runs it; while a save is in
 * flight further calls return "busy" without saving again.
 */
export function createScheduleAction<M extends ScheduledLike>(
  deps: ScheduleDeps<M>,
) {
  let inFlight = false;
  const run = async (): Promise<'ok' | 'error' | 'busy'> => {
    if (inFlight) return 'busy';
    inFlight = true;
    deps.setBusy?.(true);
    try {
      const meeting = await deps.save();
      deps.resetForm();
      deps.showScheduled({
        highlightId: meeting.id,
        message: scheduledMessage(meeting),
      });
      return 'ok';
    } catch (err) {
      deps.showError(err, () => {
        void run();
      });
      return 'error';
    } finally {
      inFlight = false;
      deps.setBusy?.(false);
    }
  };
  return run;
}

/** Move the just-scheduled meeting to the top of a list (it may sort lower). */
export function newestFirst<T extends { id: string }>(
  list: T[],
  highlightId?: string | null,
): T[] {
  if (!highlightId) return list;
  const i = list.findIndex(m => m.id === highlightId);
  if (i <= 0) return list;
  return [list[i]!, ...list.slice(0, i), ...list.slice(i + 1)];
}
